import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './AdminPanel.css';

function AdminPanel() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('hero');
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchContent();
  }, [navigate]);

  const fetchContent = async () => {
    try {
      const response = await axios.get('/api/content');
      setContent(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching content:', error);
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.put('/api/content', content, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Content updated successfully!');
    } catch (error) {
      alert('Error updating content');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    navigate('/admin/login');
  };

  const updateContent = (path, value) => {
    const keys = path.split('.');
    const newContent = { ...content };
    let current = newContent;
    
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
    
    setContent(newContent);
  };

  const handleImageUpload = async (file, path) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('image', file);
    
    // Get the old image URL to delete it on the backend
    const pathParts = path.split('.');
    let oldImageUrl = content;
    for (const part of pathParts) {
      if (oldImageUrl && typeof oldImageUrl === 'object') {
        oldImageUrl = oldImageUrl[part];
      }
    }
    
    if (oldImageUrl && oldImageUrl.startsWith('/uploads/')) {
      formData.append('oldImageUrl', oldImageUrl);
    }
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/content/upload', formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      updateContent(path, response.data.url);
      alert('Image uploaded successfully!');
    } catch (error) {
      alert('Error uploading image: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const addMenuItem = () => {
    const newItem = {
      name: 'New Item',
      description: 'Description',
      price: '₹100',
      category: 'Main Course',
      image: 'https://via.placeholder.com/400'
    };
    setContent({
      ...content,
      menu: {
        ...content.menu,
        items: [...content.menu.items, newItem]
      }
    });
  };

  const removeMenuItem = (index) => {
    const newItems = content.menu.items.filter((_, i) => i !== index);
    setContent({
      ...content,
      menu: {
        ...content.menu,
        items: newItems
      }
    });
  };

  const addSpecialty = () => {
    const newSpecialty = {
      title: 'New Feature',
      description: 'Description',
      icon: '⭐'
    };
    setContent({
      ...content,
      specialties: [...content.specialties, newSpecialty]
    });
  };

  const removeSpecialty = (index) => {
    const newSpecialties = content.specialties.filter((_, i) => i !== index);
    setContent({
      ...content,
      specialties: newSpecialties
    });
  };

  if (loading) return <div className="admin-loading">Loading...</div>;
  if (!content) return <div className="admin-error">Error loading content</div>;

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h1>Admin Panel - Chai High</h1>
        <div className="admin-actions">
          <span>Welcome, {localStorage.getItem('username')}</span>
          <button onClick={handleSave} className="btn save-btn">Save Changes</button>
          <button onClick={handleLogout} className="btn logout-btn">Logout</button>
        </div>
      </div>

      <div className="admin-content">
        <div className="admin-tabs">
          <button 
            className={`tab ${activeTab === 'hero' ? 'active' : ''}`}
            onClick={() => setActiveTab('hero')}
          >
            Hero Section
          </button>
          <button 
            className={`tab ${activeTab === 'about' ? 'active' : ''}`}
            onClick={() => setActiveTab('about')}
          >
            About
          </button>
          <button 
            className={`tab ${activeTab === 'menu' ? 'active' : ''}`}
            onClick={() => setActiveTab('menu')}
          >
            Menu
          </button>
          <button 
            className={`tab ${activeTab === 'specialties' ? 'active' : ''}`}
            onClick={() => setActiveTab('specialties')}
          >
            Specialties
          </button>
          <button 
            className={`tab ${activeTab === 'contact' ? 'active' : ''}`}
            onClick={() => setActiveTab('contact')}
          >
            Contact
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'hero' && (
            <div className="section-editor">
              <h2>Hero Section</h2>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={content.heroSection.title}
                  onChange={(e) => updateContent('heroSection.title', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Subtitle</label>
                <input
                  type="text"
                  value={content.heroSection.subtitle}
                  onChange={(e) => updateContent('heroSection.subtitle', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Background Image</label>
                <input
                  type="text"
                  value={content.heroSection.backgroundImage}
                  onChange={(e) => updateContent('heroSection.backgroundImage', e.target.value)}
                  placeholder="Enter image URL or upload below"
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e.target.files[0], 'heroSection.backgroundImage')}
                  disabled={uploading}
                />
                {content.heroSection.backgroundImage && (
                  <img src={content.heroSection.backgroundImage} alt="Preview" style={{width: '200px', marginTop: '10px'}} />
                )}
              </div>
            </div>
          )}

          {activeTab === 'about' && (
            <div className="section-editor">
              <h2>About Section</h2>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={content.about.title}
                  onChange={(e) => updateContent('about.title', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  rows="10"
                  value={content.about.description}
                  onChange={(e) => updateContent('about.description', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Image</label>
                <input
                  type="text"
                  value={content.about.image}
                  onChange={(e) => updateContent('about.image', e.target.value)}
                  placeholder="Enter image URL or upload below"
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e.target.files[0], 'about.image')}
                  disabled={uploading}
                />
                {content.about.image && (
                  <img src={content.about.image} alt="Preview" style={{width: '200px', marginTop: '10px'}} />
                )}
              </div>
            </div>
          )}

          {activeTab === 'menu' && (
            <div className="section-editor">
              <h2>Menu Items</h2>
              <button onClick={addMenuItem} className="btn add-btn">Add New Item</button>
              <div className="items-list">
                {content.menu.items.map((item, index) => (
                  <div key={index} className="item-editor">
                    <div className="item-header">
                      <h3>Item {index + 1}</h3>
                      <button onClick={() => removeMenuItem(index)} className="btn remove-btn">Remove</button>
                    </div>
                    <div className="form-group">
                      <label>Name</label>
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => {
                          const newItems = [...content.menu.items];
                          newItems[index].name = e.target.value;
                          setContent({ ...content, menu: { ...content.menu, items: newItems } });
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => {
                          const newItems = [...content.menu.items];
                          newItems[index].description = e.target.value;
                          setContent({ ...content, menu: { ...content.menu, items: newItems } });
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <label>Price</label>
                      <input
                        type="text"
                        value={item.price}
                        onChange={(e) => {
                          const newItems = [...content.menu.items];
                          newItems[index].price = e.target.value;
                          setContent({ ...content, menu: { ...content.menu, items: newItems } });
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <label>Category</label>
                      <input
                        type="text"
                        value={item.category}
                        onChange={(e) => {
                          const newItems = [...content.menu.items];
                          newItems[index].category = e.target.value;
                          setContent({ ...content, menu: { ...content.menu, items: newItems } });
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <label>Image</label>
                      <input
                        type="text"
                        value={item.image}
                        onChange={(e) => {
                          const newItems = [...content.menu.items];
                          newItems[index].image = e.target.value;
                          setContent({ ...content, menu: { ...content.menu, items: newItems } });
                        }}
                        placeholder="Enter image URL or upload below"
                      />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const formData = new FormData();
                            formData.append('image', file);
                            setUploading(true);
                            const token = localStorage.getItem('token');
                            axios.post('/api/content/upload', formData, {
                              headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'multipart/form-data'
                              }
                            }).then(response => {
                              const newItems = [...content.menu.items];
                              newItems[index].image = response.data.url;
                              setContent({ ...content, menu: { ...content.menu, items: newItems } });
                              setUploading(false);
                            }).catch(error => {
                              alert('Error uploading image');
                              setUploading(false);
                            });
                          }
                        }}
                        disabled={uploading}
                      />
                      {item.image && (
                        <img src={item.image} alt="Preview" style={{width: '100px', marginTop: '5px'}} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'specialties' && (
            <div className="section-editor">
              <h2>Specialties</h2>
              <button onClick={addSpecialty} className="btn add-btn">Add New Specialty</button>
              <div className="items-list">
                {content.specialties.map((specialty, index) => (
                  <div key={index} className="item-editor">
                    <div className="item-header">
                      <h3>Specialty {index + 1}</h3>
                      <button onClick={() => removeSpecialty(index)} className="btn remove-btn">Remove</button>
                    </div>
                    <div className="form-group">
                      <label>Title</label>
                      <input
                        type="text"
                        value={specialty.title}
                        onChange={(e) => {
                          const newSpecialties = [...content.specialties];
                          newSpecialties[index].title = e.target.value;
                          setContent({ ...content, specialties: newSpecialties });
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <input
                        type="text"
                        value={specialty.description}
                        onChange={(e) => {
                          const newSpecialties = [...content.specialties];
                          newSpecialties[index].description = e.target.value;
                          setContent({ ...content, specialties: newSpecialties });
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <label>Icon (Emoji)</label>
                      <input
                        type="text"
                        value={specialty.icon}
                        onChange={(e) => {
                          const newSpecialties = [...content.specialties];
                          newSpecialties[index].icon = e.target.value;
                          setContent({ ...content, specialties: newSpecialties });
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="section-editor">
              <h2>Contact Information</h2>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="text"
                  value={content.contact.phone}
                  onChange={(e) => updateContent('contact.phone', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="text"
                  value={content.contact.email}
                  onChange={(e) => updateContent('contact.email', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={content.contact.address}
                  onChange={(e) => updateContent('contact.address', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Hours</label>
                <input
                  type="text"
                  value={content.contact.hours}
                  onChange={(e) => updateContent('contact.hours', e.target.value)}
                />
              </div>
              <h3>Social Media</h3>
              <div className="form-group">
                <label>Facebook URL</label>
                <input
                  type="text"
                  value={content.social.facebook}
                  onChange={(e) => updateContent('social.facebook', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Instagram URL</label>
                <input
                  type="text"
                  value={content.social.instagram}
                  onChange={(e) => updateContent('social.instagram', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Twitter URL</label>
                <input
                  type="text"
                  value={content.social.twitter}
                  onChange={(e) => updateContent('social.twitter', e.target.value)}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminPanel;